// src/components/widgets/WidgetConfigPanelShadcn.jsx
import React, { useState, useEffect } from 'react';
import {
  AlertCircle,
  Calendar,
  MessageCircle,
  Mail,
  Monitor,
  Sun,
  Moon,
  Search,
  DollarSign,
  Clock
} from 'lucide-react';

// shadcn components
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Textarea } from '../ui/textarea';
import { Card, CardContent } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';

// Import our shadcn components
import WidgetPreviewShadcn from './WidgetPreviewShadcn';
import FormFieldsTab from './FormFieldsTab';

import { useWidgets } from '../../hooks/useWidgets';
import { useSettings } from '../../hooks/useSettings';
import { cn } from '../../utils/cn';

const WidgetConfigPanelShadcn = ({ onShowEmbed, className = '' }) => {
  const {
    selectedWidget,
    updateWidget
  } = useWidgets();

  const { services } = useSettings();

  // This component now only handles editing
  if (!selectedWidget) {
    return null;
  }

  // Form state for editing only
  const [formData, setFormData] = useState({
    widget_name: '',
    widget_type: 'booking',
    is_active: false,
    primary_color: '#007bff',
    theme: 'auto',
    border_radius: 8,
    configuration: {
      enabled_services: [],
      require_phone: true,
      require_notes: false,
      show_how_heard: true,
      show_sms_consent: true
    }
  });

  // Simple validation state
  const [errors, setErrors] = useState({});

  // Original data for change detection
  const [originalData, setOriginalData] = useState(null);

  // Preview refresh trigger
  const [previewTrigger, setPreviewTrigger] = useState(0);

  // Service search for large service lists
  const [serviceSearch, setServiceSearch] = useState('');

  // Initialize form when widget changes
  useEffect(() => {
    if (selectedWidget) {
      const newFormData = {
        widget_name: selectedWidget.widget_name || '',
        widget_type: selectedWidget.widget_type || 'booking',
        is_active: selectedWidget.is_active ?? false,
        primary_color: selectedWidget.primary_color || '#007bff',
        theme: selectedWidget.theme || 'auto',
        border_radius: selectedWidget.border_radius || 8,
        configuration: {
          enabled_services: selectedWidget.configuration?.enabled_services || [],
          require_phone: selectedWidget.configuration?.require_phone ?? true,
          require_notes: selectedWidget.configuration?.require_notes ?? false,
          show_how_heard: selectedWidget.configuration?.show_how_heard ?? true,
          show_sms_consent: selectedWidget.configuration?.show_sms_consent ?? true,
          success_message: selectedWidget.configuration?.success_message || '',
          field_labels: selectedWidget.configuration?.field_labels || {},
          custom_questions: selectedWidget.configuration?.custom_questions || [],
          ...selectedWidget.configuration
        }
      };
      setFormData(newFormData);
      setOriginalData(newFormData);
      setErrors({});
    }
  }, [selectedWidget?.id]);

  // Direct field change handler
  const handleFieldChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field error
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: null
      }));
    }
  };

  // Direct config change handler
  const handleConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      configuration: {
        ...prev.configuration,
        [field]: value
      }
    }));
  };

  // Simple change detection
  const hasChanges = originalData ? JSON.stringify(formData) !== JSON.stringify(originalData) : false;

  // Form validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.widget_name.trim()) {
      newErrors.widget_name = 'Widget name is required';
    }
    if (!formData.widget_type) {
      newErrors.widget_type = 'Widget type is required';
    }
    if (!/^#[0-9A-F]{6}$/i.test(formData.primary_color)) {
      newErrors.primary_color = 'Invalid color format';
    }
    if (formData.widget_type === 'booking' && formData.configuration.enabled_services.length === 0) {
      newErrors.enabled_services = 'At least one service must be selected';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Auto-save with debounce
  useEffect(() => {
    if (!hasChanges || !selectedWidget) return;

    const timer = setTimeout(() => {
      if (!validateForm()) {
        console.warn('Auto-save skipped: validation failed');
        return;
      }

      console.log('Auto-saving widget changes...');
      updateWidget(selectedWidget.id, {
        widget_name: formData.widget_name,
        is_active: formData.is_active,
        primary_color: formData.primary_color,
        theme: formData.theme,
        border_radius: formData.border_radius,
        configuration: formData.configuration
      });

      setOriginalData({ ...formData });
    }, 2000); // Auto-save 2 seconds after last change

    return () => clearTimeout(timer);
  }, [formData, hasChanges, selectedWidget]);

  // Refresh preview (debounced)
  const refreshPreview = () => {
    setPreviewTrigger(prev => prev + 1);
  };

  // Auto-refresh preview when widget name changes (with debounce)
  useEffect(() => {
    const timer = setTimeout(() => {
      if (formData.widget_name) {
        refreshPreview();
      }
    }, 1000); // Only refresh after user stops typing for 1 second

    return () => clearTimeout(timer);
  }, [formData.widget_name]);

  // Widget types for display (editing doesn't allow type changes)
  const widgetTypes = [
    {
      id: 'booking',
      name: 'Booking Widget',
      description: 'Allow customers to book appointments',
      icon: Calendar,
      available: true
    },
    {
      id: 'chat',
      name: 'Chat Widget',
      description: 'Live chat for customer support',
      icon: MessageCircle,
      available: false
    },
    {
      id: 'contact',
      name: 'Contact Widget',
      description: 'Simple contact form for inquiries',
      icon: Mail,
      available: false
    }
  ];

  // Theme options
  const themeOptions = [
    { value: 'auto', label: 'Auto', icon: Monitor },
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon }
  ];

  // Preset colors
  const presetColors = [
    '#007bff', '#28a745', '#dc3545', '#ffc107', '#6f42c1',
    '#fd7e14', '#20c997', '#e83e8c', '#6c757d', '#343a40'
  ];

  // Filter services for selector
  const filteredServices = services.filter(service => {
    if (!service.active) return false;
    if (!serviceSearch) return true;

    const searchLower = serviceSearch.toLowerCase();
    return (
      service.name.toLowerCase().includes(searchLower) ||
      (service.description && service.description.toLowerCase().includes(searchLower))
    );
  });

  // Service selection handlers
  const handleServiceToggle = (serviceId) => {
    const newSelection = formData.configuration.enabled_services.includes(serviceId)
      ? formData.configuration.enabled_services.filter(id => id !== serviceId)
      : [...formData.configuration.enabled_services, serviceId];

    handleConfigChange('enabled_services', newSelection);
  };

  const formatPrice = (price) => {
    if (!price) return 'Contact for pricing';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(price);
  };

  const formatDuration = (minutes) => {
    if (!minutes) return 'Duration varies';
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours > 0 && mins > 0) {
      return `${hours}h ${mins}m`;
    } else if (hours > 0) {
      return `${hours}h`;
    } else {
      return `${mins}m`;
    }
  };

  const currentWidgetType = widgetTypes.find(type => type.id === formData.widget_type);

  return (
    <div className={cn("h-full flex", className)}>
      {/* Left Sidebar - Configuration */}
      <div className="w-[480px] border-r flex flex-col bg-card">
        <Tabs defaultValue="basic" className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 px-4 pt-4 pb-4">
            <TabsList className="grid w-full grid-cols-4">
              <TabsTrigger value="basic">Basic</TabsTrigger>
              <TabsTrigger value="appearance">Style</TabsTrigger>
              <TabsTrigger value="services">Services</TabsTrigger>
              <TabsTrigger value="form-fields">Form Fields</TabsTrigger>
            </TabsList>
          </div>

          <div className="flex-1 min-h-0 overflow-y-auto">
            <TabsContent value="basic" className="mt-0 p-4 space-y-4">
                {/* Widget Name */}
                <div className="space-y-2">
                  <Label htmlFor="widget-name">Widget Name</Label>
                  <Input
                    id="widget-name"
                    value={formData.widget_name}
                    onChange={(e) => handleFieldChange('widget_name', e.target.value)}
                    placeholder="Enter widget name"
                  />
                  {errors.widget_name && (
                    <Alert variant="destructive">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>{errors.widget_name}</AlertDescription>
                    </Alert>
                  )}
                </div>

                {/* Widget Type - Display Only */}
                {currentWidgetType && (
                  <div className="space-y-2">
                    <Label>Widget Type</Label>
                    <Card className="border-primary bg-primary/5">
                      <CardContent className="p-3">
                        <div className="flex items-start gap-3">
                          <div className="p-2 rounded-lg bg-primary/20 text-primary">
                            <currentWidgetType.icon className="w-4 h-4" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-medium text-sm">{currentWidgetType.name}</h4>
                            <p className="text-xs text-muted-foreground">{currentWidgetType.description}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                )}

                {/* Widget Status */}
                <div className="flex items-center space-x-2">
                  <Switch
                    id="is-active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => handleFieldChange('is_active', checked)}
                  />
                  <Label htmlFor="is-active" className="text-sm">Active (visible on your website)</Label>
                </div>

                {/* Success Message */}
                <div className="space-y-2">
                  <Label htmlFor="success-message">Success Message</Label>
                  <Textarea
                    id="success-message"
                    value={formData.configuration.success_message || ''}
                    onChange={(e) => handleConfigChange('success_message', e.target.value)}
                    placeholder="Your appointment has been scheduled. You'll receive a confirmation email shortly."
                    rows={3}
                    className="resize-none"
                  />
                  <p className="text-xs text-muted-foreground">
                    Customize the message shown after successful booking. Leave empty to use the default message.
                  </p>
                </div>
              </TabsContent>

              <TabsContent value="appearance" className="mt-0 p-4 space-y-4">
                {/* Primary Color */}
                <div className="space-y-2">
                  <Label>Primary Color</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="color"
                      value={formData.primary_color}
                      onChange={(e) => handleFieldChange('primary_color', e.target.value)}
                      className="w-16 h-10 p-1"
                    />
                    <Input
                      value={formData.primary_color}
                      onChange={(e) => handleFieldChange('primary_color', e.target.value)}
                      placeholder="#007bff"
                      className="flex-1"
                    />
                  </div>
                  {/* Preset Colors */}
                  <div className="grid grid-cols-5 gap-2">
                    {presetColors.map(color => (
                      <button
                        key={color}
                        onClick={() => handleFieldChange('primary_color', color)}
                        className={cn(
                          "w-8 h-8 rounded border-2 transition-all",
                          formData.primary_color === color ? "border-primary scale-110" : "border-border"
                        )}
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>

                {/* Theme */}
                <div className="space-y-3">
                  <Label>Theme</Label>
                  <RadioGroup
                    value={formData.theme}
                    onValueChange={(value) => handleFieldChange('theme', value)}
                  >
                    {themeOptions.map(option => {
                      const Icon = option.icon;
                      return (
                        <div key={option.value} className="flex items-center space-x-2">
                          <RadioGroupItem value={option.value} id={option.value} />
                          <Label htmlFor={option.value} className="flex items-center gap-2 cursor-pointer text-sm">
                            <Icon className="w-4 h-4" />
                            {option.label}
                          </Label>
                        </div>
                      );
                    })}
                  </RadioGroup>
                </div>
              </TabsContent>

              <TabsContent value="services" className="mt-0 p-4 space-y-4">
                {/* Services Selection */}
                {formData.widget_type === 'booking' && (
                  <div className="space-y-4">
                    <div className="space-y-2">
                      <Label>Services</Label>
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                        <Input
                          placeholder="Search services..."
                          value={serviceSearch}
                          onChange={(e) => setServiceSearch(e.target.value)}
                          className="pl-10"
                        />
                      </div>
                    </div>

                    {errors.enabled_services && (
                      <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{errors.enabled_services}</AlertDescription>
                      </Alert>
                    )}

                    <div className="space-y-2">
                      {filteredServices.map(service => (
                        <Card key={service.id} className="cursor-pointer" onClick={() => handleServiceToggle(service.id)}>
                          <CardContent className="p-3">
                            <div className="flex items-center space-x-3">
                              <input
                                type="checkbox"
                                checked={formData.configuration.enabled_services.includes(service.id)}
                                onChange={() => handleServiceToggle(service.id)}
                                className="rounded border-gray-300"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between">
                                  <h4 className="font-medium text-sm truncate">{service.name}</h4>
                                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                    <div className="flex items-center gap-1">
                                      <Clock className="w-3 h-3" />
                                      {formatDuration(service.duration)}
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <DollarSign className="w-3 h-3" />
                                      {formatPrice(service.price)}
                                    </div>
                                  </div>
                                </div>
                                {service.description && (
                                  <p className="text-xs text-muted-foreground mt-1 truncate">
                                    {service.description}
                                  </p>
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      ))}
                    </div>

                    {filteredServices.length === 0 && (
                      <div className="text-center py-8 text-sm text-muted-foreground">
                        {serviceSearch ? 'No services match your search' : 'No active services found'}
                      </div>
                    )}
                  </div>
                )}
              </TabsContent>

              <TabsContent value="form-fields" className="mt-0 p-4 space-y-4">
                {/* Form Fields Configuration - Unified */}
                {formData.widget_type === 'booking' && (
                  <FormFieldsTab
                    requirePhone={formData.configuration.require_phone}
                    requireNotes={formData.configuration.require_notes}
                    showHowHeard={formData.configuration.show_how_heard}
                    showSmsConsent={formData.configuration.show_sms_consent}
                    onRequirePhoneChange={(value) => handleConfigChange('require_phone', value)}
                    onRequireNotesChange={(value) => handleConfigChange('require_notes', value)}
                    onShowHowHeardChange={(value) => handleConfigChange('show_how_heard', value)}
                    onShowSmsConsentChange={(value) => handleConfigChange('show_sms_consent', value)}
                    fieldLabels={formData.configuration.field_labels || {}}
                    onFieldLabelsChange={(newLabels) => handleConfigChange('field_labels', newLabels)}
                    customQuestions={formData.configuration.custom_questions || []}
                    onCustomQuestionsChange={(newQuestions) => handleConfigChange('custom_questions', newQuestions)}
                  />
                )}
              </TabsContent>
            </div>
          </Tabs>
        </div>

      {/* Center - Preview */}
      <div className="flex-1 bg-muted/30">
        <WidgetPreviewShadcn
          widget={{ ...selectedWidget, ...formData }}
          refreshTrigger={previewTrigger}
          isIntegrated={false}
        />
      </div>
    </div>
  );
};

export default WidgetConfigPanelShadcn;
